import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { generateSecureRegistrationLink, PatientInput, SecureRegistrationLink } from '../../api/admin';

interface WelcomeEmailConfirmationModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (email: string, language: string, generatedLink: {link: string}) => Promise<void>;
  patientName: string;
  patientEmail: string;
  patientData: PatientInput;
  loading?: boolean;
}

export function WelcomeEmailConfirmationModal({ 
  show, 
  onHide, 
  onConfirm, 
  patientName, 
  patientEmail, 
  patientData,
  loading = false 
}: WelcomeEmailConfirmationModalProps) {

  const [email, setEmail] = useState(patientEmail || '');
  const [language, setLanguage] = useState('el');
  const [error, setError] = useState('');
  const [generatedLink, setGeneratedLink] = useState<SecureRegistrationLink | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Update email state when patientEmail prop changes
  useEffect(() => {
    if (patientEmail) {
      setEmail(patientEmail);
    } else if (patientData?.email) {
      setEmail(patientData.email);
    }
  }, [patientEmail, patientData?.email]);

  // Don't auto-generate - let user generate when ready
  // This prevents database pollution from language changes

  const generateSecureLink = async () => {
    try {
      setIsGenerating(true);
      setError('');
      
      // Update patient data with confirmed email
      const finalPatientData = {
        ...patientData,
        email: email.trim()
      };
      

      
      // Generate secure registration link with OTP
      const linkData = await generateSecureRegistrationLink(finalPatientData, language);
      setGeneratedLink(linkData);
      
    } catch (err: any) {
      console.error('Error generating secure registration link:', err);
      setError('Failed to generate secure registration link: ' + (err.message || 'Unknown error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Auto-generate link if it doesn't exist
    if (!generatedLink) {
      setError('Generating secure link...');
      await generateSecureLink();
      // The link generation will complete and then we can proceed
      // Note: This will be handled by the generated link state change
      return;
    }

    // Send email with the generated link
    setError('');
    await onConfirm(email, language, {
      link: generatedLink.link
    });
  };

  const handleClose = () => {
    setEmail(patientEmail || '');
    setLanguage('el');
    setError('');
    setGeneratedLink(null);
    setIsGenerating(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-envelope-heart me-2"></i>
          Send Welcome Email & Registration Link
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <div className="alert alert-info">
          <h6 className="mb-2">
            <i className="bi bi-info-circle me-2"></i>
            Patient Registration Setup
          </h6>
          <p className="mb-2">
            {isGenerating ? 'Generating secure registration link...' : 'A secure registration link will be generated and'} a welcome email will be sent to <strong>{patientName}</strong> with their personalized registration link. When they open the link, they will receive an OTP code via email to complete the verification.
          </p>
          {patientEmail && (
            <p className="mb-2 small text-success">
              <i className="bi bi-check-circle me-1"></i>
              Patient email: <strong>{patientEmail}</strong>
            </p>
          )}
          {!patientEmail && (
            <p className="mb-2 small text-warning">
              <i className="bi bi-exclamation-triangle me-1"></i>
              Please provide the patient's email address.
            </p>
          )}
          <p className="mb-0 small">
            The patient will receive a secure 72-hour registration link where they can review and complete their information in a comprehensive form with their pre-filled data from this session.
          </p>
        </div>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="bi bi-envelope me-2"></i>
                  Patient Email Address
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="patient@example.com"
                  disabled={loading}
                  className={error ? 'is-invalid' : ''}
                />
                <Form.Text className="text-muted">
                  The patient will receive the welcome email at this address.
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <i className="bi bi-translate me-2"></i>
                  Email Language
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={loading}
                >
                  <option value="el">🇬🇷 Ελληνικά (Greek)</option>
                  <option value="en">🇺🇸 English</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Select the language for the welcome email content.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="mt-4">
            <h6 className="mb-3">
              <i className="bi bi-person-check me-2"></i>
              Patient Information:
            </h6>
            <div className="card border-primary">
              <div className="card-body p-3">
                <div className="row">
                  <div className="col-md-6 mb-2">
                    <strong>Patient Name:</strong>
                    <div className="text-muted">{patientName}</div>
                  </div>
                  <div className="col-md-6 mb-2">
                    <strong>Email Address:</strong>
                    <div className="text-muted">{email || 'Not specified'}</div>
                  </div>

                </div>
                
                {isGenerating && (
                  <div className="mt-3 text-center">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    <span className="text-muted">Generating secure registration link...</span>
                  </div>
                )}

                {!generatedLink && !isGenerating && (
                  <div className="mt-3 text-center">
                    <button 
                      type="button"
                      className="btn btn-primary"
                      onClick={generateSecureLink}
                      disabled={!email.trim()}
                    >
                      <i className="bi bi-shield-lock me-2"></i>
                      Generate Secure Link
                    </button>
                    <div className="small text-muted mt-2">
                      Click to generate a secure registration link with the selected language ({language === 'el' ? 'Greek' : 'English'})
                    </div>
                  </div>
                )}

                {generatedLink && (
                  <div className="mt-3">
                    <strong>🔒 Secure Registration Link Generated:</strong>
                    <div className="mt-2 p-3 border rounded bg-light">
                      <div className="mb-2">
                        <label className="form-label small text-muted">Registration Link:</label>
                        <div className="input-group">
                          <input 
                            type="text" 
                            className="form-control font-monospace" 
                            value={generatedLink.link} 
                            readOnly 
                          />
                          <button 
                            className="btn btn-outline-secondary" 
                            type="button"
                            onClick={() => navigator.clipboard.writeText(generatedLink.link)}
                          >
                            <i className="bi bi-clipboard"></i>
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-3 text-center">
                        <button 
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={generateSecureLink}
                          disabled={isGenerating || loading}
                        >
                          <i className="bi bi-arrow-clockwise me-1"></i>
                          {isGenerating ? 'Generating...' : 'Regenerate Link'}
                        </button>
                        <div className="small text-muted mt-1">
                          Use this if you want to generate a new link or changed the language
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="alert alert-success mt-3">
            <h6 className="mb-2">
              <i className="bi bi-check-circle me-2"></i>
              What the patient will receive:
            </h6>
            <ul className="mb-0 small">
              <li>Professional welcome message from their healthcare provider</li>
              <li>Secure link to access their patient registration</li>
              <li>Clear instructions on the OTP verification process</li>
              <li>Information about privacy and security measures</li>
              <li>Contact information for support if needed</li>
            </ul>
          </div>

          <div className="alert alert-warning mt-3">
            <h6 className="mb-2">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Important Notes - New Two-Step Registration System
            </h6>
            <ul className="mb-0 small">
              <li><strong>Two-Step Security:</strong> Patient clicks the link and receives a 6-digit OTP code via email</li>
              <li><strong>Link Validity:</strong> The registration link is valid for 72 hours</li>
              <li><strong>OTP Process:</strong> OTP is sent automatically when patient opens the link (2-minute expiration)</li>
              <li><strong>Session Management:</strong> After successful OTP verification, patient gets a 24-hour session</li>
              <li><strong>Progress Saving:</strong> Patient can save progress and return within the session period</li>
              <li><strong>Security Features:</strong> Failed OTP attempts are tracked; link locks after 5 failures</li>
            </ul>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handleClose}
          disabled={loading}
          style={{ 
            width: '120px', 
            height: '38px',
            padding: '6px 12px',
            fontSize: '14px'
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="success" 
          onClick={handleSubmit}
          disabled={(loading || isGenerating) || !email.trim() || !generatedLink}
          style={{ 
            width: '200px', 
            height: '38px',
            padding: '6px 12px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          <i className="bi bi-envelope-heart me-2"></i>
          Send Email to Patient
          {(loading || isGenerating) && (
            <span 
              className="spinner-border spinner-border-sm" 
              role="status" 
              aria-hidden="true"
              style={{
                marginLeft: '8px',
                flexShrink: 0
              }}
            ></span>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}